import { useState, useEffect } from "react";
import "./SignUp.scss";
import Auth_Logo from "../../assets/images/logo/logo.svg";
import Eye from "../../assets/images/vactor/eye.svg";
import EyeLine from "../../assets/images/vactor/eye-line.svg";
import Right_Arrow from "../../assets/images/vactor/arrow-right.svg";
import { Autocomplete, IconButton, InputAdornment, ListItem, ListItemText, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PrimaryButton } from "../../components/AllButtons/AllButtons";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch } from "react-redux";
import { signUpPostCall, SignUpPayload } from "../../Services/auth.service";
import { AppDispatch } from "../../Store/store";
import { openSnackbar } from "../../Slice/snackbarSlice";
import TitleComponent from "../../components/CommonElements/TitleComponent/TitleComponent";
import { getUserDetails } from "../../Services/profile.service";
import { login } from "../../Slice/userSlice";
import Loader from "../../components/Loader/Loader/Loader";

const selectGrade = [{ label: "Grade 9" }, { label: "Grade 10" }, { label: "Grade 11" }, { label: "Grade 12" }];
const initialValues = {
  name: "",
  email: "",
  password: "",
  confirm_password: "",
  institution: "",
  grade: null,
  birth_date: null,
};
const minBirthYear = 1970;
const minBirthDate = dayjs(`${minBirthYear}-01-01`);
const maxBirthDate = dayjs().subtract(5, "year").endOf("year");

const validationSchema = Yup.object({
  name: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .test(
      "no-only-whitespace",
      "Name cannot be empty or only whitespace",
      (value) => !!value && value.trim().length > 0
    )
    .test(
      "is-not-number",
      "Name cannot be a number",
      (value: any) => !/^\d+$/.test(value)
    )
    .test(
      "no-multiple-spaces",
      "Name cannot contain multiple consecutive spaces",
      (value) => !!value && !/\s{2,}/.test(value)
    )
    .min(5, "Name must be at least 5 characters")
    .max(35, "Name cannot exceed 35 characters")
    .required("Full Name is required"),
  institution: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .test(
      "no-only-whitespace",
      "Institution/School cannot be empty or only whitespace",
      (value) => !!value && value.trim().length > 0
    )
    .min(2, "Institution/School must be at least 2 characters")
    .max(255, "Institution/School cannot exceed 255 characters")
    .required("Institution/School is required"),
  grade: Yup.number()
    .required("Grade is required")
    .oneOf([9, 10, 11, 12], "Please select a valid grade")
    .typeError("Please select a grade"),
  birth_date: Yup.mixed()
    .required("Date of Birth is required")
    .test('is-valid-date', 'Invalid Date', (value: any) => {
      if (!value) return false;
      const date = dayjs.isDayjs(value) ? value : dayjs(value, "MM/DD/YYYY", true);
      return date.isValid();
    })
    .test('is-eligible-birth-year', 'Age must meet the registration eligibility criteria(e.g.,greater than 1970)', (value: any) => {
      if (!value) return false;
      const date = dayjs.isDayjs(value) ? value : dayjs(value, "MM/DD/YYYY", true);
      return date.isValid() && date.year() >= minBirthYear;
    })
    .test('is-not-current-year', 'Birth year cannot be the current year', (value: any) => {
      if (!value) return false;
      const date = dayjs.isDayjs(value) ? value : dayjs(value, "MM/DD/YYYY", true);
      return date.isValid() && date.year() < dayjs().year();
    })
    .test('is-past-date', 'Date cannot be in the future', (value: any) => {
      if (!value) return false;
      const date = dayjs.isDayjs(value) ? value : dayjs(value, "MM/DD/YYYY", true);
      return date.isValid() && date.isBefore(dayjs());
    }),
  email: Yup.string()
    .test(
      "no-only-whitespace",
      "Email cannot be empty or only whitespace",
      (value) => !!value && value.trim().length > 0
    )
    .email("Invalid email address")
    .required("Email is required")
    .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, "Invalid email format"),
  password: Yup.string().transform((value) => (value ? value.trim() : value)).test(
    "no-only-whitespace",
    "Password cannot be empty or only whitespace",
    (value) => !!value && value.trim().length > 0
  ).matches(/^\S*$/, "Password cannot contain spaces")
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirm_password: Yup.string().transform((value) => (value ? value.trim() : value)).test(
    "no-only-whitespace",
    "Confirm Password cannot be empty or only whitespace",
    (value) => !!value && value.trim().length > 0
  ).matches(/^\S*$/, "Confirm Password cannot contain spaces")
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

type FormValues = typeof initialValues;

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'auth-token' && e.newValue) {
        const role = localStorage.getItem("role");
        if (role === "admin") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [navigate]);

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, action) => {
      const token = localStorage.getItem("auth-token");
      const role = localStorage.getItem("role");
      if (token) {
        if (role === "admin") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
        return;
      }
      Object.keys(initialValues).forEach((key) => {
        setFieldTouched(key as keyof FormValues, true, true);
      });

      const formattedValues: SignUpPayload = {
        ...values,
        institution: values.institution.trim(),
        birth_date: values.birth_date ? dayjs(values.birth_date).format("MM/DD/YYYY") : null
      };
      setLoading(true);
      dispatch(signUpPostCall(formattedValues))
        .unwrap()
        .then((response) => {
          if (response.status) {
            dispatch(openSnackbar({ message: response.message, severity: "success" }))
            dispatch(login(response))
            dispatch(getUserDetails(response.data.id))
            navigate("/dashboard")
          }
          else {
            setLoading(false);
            let message = response?.message;

            if (message.toLowerCase() === "user already exists") {
              message = "User Already Exists";
            }
            dispatch(openSnackbar({ message: message || "Error in signing the user", severity: "error" }))
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error("Something went wrong in signing In", error);
          dispatch(openSnackbar({ message: "Something went wrong", severity: "error" }));
        })
    },
  });
 
  const isFormValid = validationSchema.isValidSync(values, { abortEarly: false });

  useEffect(() => {
    if (values.confirm_password) {
      setFieldTouched('confirm_password', true, true);
    }
  }, [values.password, values.confirm_password, setFieldTouched]);

  const handleDateChange = async (newValue: Dayjs | null) => {
    await setFieldValue("birth_date", newValue, true);
    setFieldTouched("birth_date", true, false);
  };

  const handleGradeChange = (grade: string) => {
    const match = grade.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleImmediateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e);
    setFieldTouched(e.target.name as keyof FormValues, true, false);
  };

  const handleImmediateBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldTouched(e.target.name as keyof FormValues, true, true);
    handleBlur(e);
  };
  const isSignUpDisabled = !isFormValid || loading;

  if(loading){
    return <Loader />;
  }
  return (
    <>
      <div className="auth-wrapper">
        <div className="row mx-0 flex-wrap-reverse">
          <TitleComponent />
          <div className="col-md-12 col-lg-5 col-xl-4 p-0">
            <div className="auth-form-block">
              <div className="auth-form-content-block">
                <div className="auth-logo">
                  <img src={Auth_Logo} alt="" />
                </div>
                <h4 className="banner-content-title">Sign Up</h4>
                <p className="content-dsc-text">Please complete all required fields to create your account.</p>
                <form onSubmit={handleSubmit} className="w-100">
                  {/* Name Field */}
                  <div className="input-form-field-wrapper">
                    <TextField
                      className="out-label-input-text"
                      id="name"
                      name="name"
                      placeholder="Full Name"
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      type="text"
                      value={values.name}
                      onChange={handleImmediateChange}
                      onBlur={handleImmediateBlur}
                      error={touched.name && Boolean(errors.name)}
                    />
                    {errors.name && touched.name && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="input-form-field-wrapper">
                    <TextField
                      className="out-label-input-text"
                      id="institution"
                      name="institution"
                      placeholder="Institution / School"
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      type="text"
                      value={values.institution}
                      onChange={handleImmediateChange}
                      onBlur={handleImmediateBlur}
                      error={touched.institution && Boolean(errors.institution)}
                    />
                    {errors.institution && touched.institution && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.institution}
                      </p>
                    )}
                  </div>

                  {/* Grade Field */}
                  <div className="input-form-field-wrapper">
                    <Autocomplete
                      className="grade"
                      id="grade"
                      options={selectGrade}
                      getOptionLabel={(option) => option.label}
                      value={
                        values.grade
                          ? selectGrade.find((option) => handleGradeChange(option.label) === values.grade)
                          : null
                      }
                      onChange={(_, newValue) => {
                        const gradeNumber = newValue ? handleGradeChange(newValue.label) : null;
                        setFieldValue("grade", gradeNumber);
                        setFieldTouched("grade", true, false);
                      }}
                      onBlur={() => setFieldTouched("grade", true, true)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="out-label-input-text"
                          placeholder="Grade"
                          margin="normal"
                          variant="outlined"
                          error={touched.grade && Boolean(errors.grade)}
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem className="autocomplete-list-items" {...props}>
                          <ListItemText primary={option.label} />
                        </ListItem>
                      )}
                    />
                    {errors.grade && touched.grade && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.grade}
                      </p>
                    )}
                  </div>

                  {/* Date of Birth Field */}
                  <div className="input-form-field-wrapper datepicker-field-wrapper">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values.birth_date ? dayjs(values.birth_date) : null}
                        onChange={handleDateChange}
                        format="MM/DD/YYYY"
                        disableOpenPicker={false}
                        disableFuture
                        minDate={minBirthDate}
                        maxDate={maxBirthDate}
                        slotProps={{
                          textField: {
                            className: "out-label-input-text",
                            margin: "normal",
                            variant: "outlined",
                            fullWidth: true,
                            onBlur: () => setFieldTouched("birth_date", true),
                            placeholder: "Date of Birth",
                            error: touched.birth_date && Boolean(errors.birth_date),
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {errors.birth_date && touched.birth_date && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.birth_date}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="input-form-field-wrapper" style={{ display: "flex", flexDirection: "column" }}>
                    <TextField
                      className="out-label-input-text"
                      id="email"
                      name="email"
                      placeholder="Email"
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      type="email"
                      value={values.email}
                      onChange={handleImmediateChange}
                      onBlur={handleImmediateBlur}
                      error={touched.email && Boolean(errors.email)}
                    />
                    {errors.email && touched.email && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="input-form-field-wrapper">
                    <TextField
                      id="pass"
                      className="out-label-input-text"
                      name="password"
                      placeholder="Password"
                      margin="normal"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={(e) => {
                        handleImmediateChange(e);
                        if (values.confirm_password) {
                          setFieldTouched('confirm_password', true, true);
                        }
                      }}
                      onBlur={handleImmediateBlur}
                      fullWidth
                      error={touched.password && Boolean(errors.password)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              edge="end"
                              onClick={handleTogglePassword}
                            >
                              <img src={showPassword ? Eye : EyeLine} alt="Toggle password" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.password && touched.password && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="input-form-field-wrapper">
                    <TextField
                      className="out-label-input-text"
                      name="confirm_password"
                      placeholder="Confirm Password"
                      margin="normal"
                      variant="outlined"
                      type={showConfirmPassword ? "text" : "password"}
                      value={values.confirm_password}
                      onChange={handleImmediateChange}
                      onBlur={handleImmediateBlur}
                      fullWidth
                      error={touched.confirm_password && Boolean(errors.confirm_password)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              edge="end"
                              onClick={handleToggleConfirmPassword}
                            >
                              <img src={showConfirmPassword ? Eye : EyeLine} alt="Toggle password" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {errors.confirm_password && touched.confirm_password && (
                      <p className="form-error" style={{
                        fontSize: "12px",
                        color: "red",
                        marginLeft: "0",
                        fontWeight: "normal",
                        alignSelf: "flex-start",
                        marginTop: "-5px",
                      }}>
                        {errors.confirm_password}
                      </p>
                    )}
                  </div>

                  <div className="auth-action-control">
                    <PrimaryButton
                      className="signup-submit-btn"
                      type="submit"
                      disabled={isSignUpDisabled}
                    >
                      Sign Up
                      <img src={Right_Arrow} alt="Right_Arrow" />
                    </PrimaryButton>
                  </div>
                  <div className="auth-info-text">
                    Already have an account?{" "}
                    <Link className="auth-link" to="/login">
                      Sign in
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;
